import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsyaml from 'js-yaml';
import csvToJson from 'csvtojson';
import Papa from 'papaparse';
import Editor from '@monaco-editor/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faCloudArrowDown, faEraser, faCopy, faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

const objectSampleData = `
{
  title: {
    type: 'String',
    trim: true,
    index: true,
    required: true
  },
  year: {
    type: 'Number',
    max: 2012,
    validate: 'validateBookYear'
  },
  author: {
    type: 'ObjectId',
    ref: 'Author',
    index: true,
    required: true
  }
}`;

const ConvertDataType = (props) => {
  const { convertName, editorTheme } = props;
  const navigate = useNavigate();
  const getData = (convertName) => {
    const splitDt = convertName.split(':')
    return {
      inputName: splitDt[0], outputName: splitDt[1]
    }
  }
  const inputName = getData(convertName)['inputName']
  const outputName = getData(convertName)['outputName']
  const hasUploadFile = !['OBJECT'].includes(inputName)
  const defaultInput = ['OBJECT'].includes(inputName) ? objectSampleData : ''
  const [originalInput, setOriginalInput] = useState(defaultInput);
  const [convertedOutput, setConvertedOutput] = useState('');
  const [currentConvertName, setCurrentConvertName] = useState(convertName);

  const showErrorMessage = (errorMessage) => {
    toast.error(errorMessage, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  }

  const convertDataTypeToEditorLanguage = (dataType) => {
    const mappings = {
      "JSON": "json",
      "CSV": "csv",
      "YAML": "yaml",
      "OBJECT": "ruby",
      "JSON MINIFIER": "json",
      "URL": "text",
      "ENCODED URL": "text",
    }
    return mappings[dataType]
  }

  const convertDataTypeToFileExt = (dataType) => {
    const mappings = {
      "JSON": ".json",
      "CSV": ".csv",
      "YAML": ".yaml",
      "OBJECT": ".rb",
      "JSON MINIFIER": ".json",
      "URL": ".txt",
      "ENCODED URL": ".txt",
    }
    return mappings[dataType]
  } 

  const handleConvertData = () => {
    switch(convertName) {
      case "YAML:JSON":
        convertYamlToJson()
        break;
      case "JSON:YAML":
        convertJsonToYaml()
        break;
      case "CSV:JSON":
        convertCsvToJson()
        break;
      case "JSON:CSV":
        convertJsonToCsv()
        break;
      case "OBJECT:JSON":
        convertStringToJson()
        break;
      case "JSON:JSON":
        handleBeautifulJson()
        break;
      case "JSON:JSON MINIFIER":
        handleMinifyJson()
        break;
      case "URL:ENCODED URL":
        handleEncode()
        break;
      case "ENCODED URL:URL":
        handleDecode()
        break;
      default:
    }
  }

  const handleEncode = () => {
    setConvertedOutput(encodeURIComponent(originalInput));
  };

  const handleDecode = () => {
    setConvertedOutput(decodeURIComponent(originalInput));
  };

  const handleSwitch = () => {
    let navigateUrl = '/'
    switch(convertName) {
      case "URL:ENCODED URL":
        navigateUrl = '/url-decode'
        break;
      case "ENCODED URL:URL":
        navigateUrl = '/url-encode'
        break;
      default:
    }
    navigate(navigateUrl);
  }

  const convertStringToJson = () => {
    let inputString = originalInput
    // Remove extra whitespace
    inputString = inputString.replace(/\s/g, '');
  
    // Remove newlines and tabs
    inputString = inputString.replace(/[\r\n\t]/g, '');
  
    // Remove comments (optional)
    inputString = inputString.replace(/\/\/.*?(\r\n|\n|$)/g, '');
  
    // Replace single quotes with double quotes
    inputString = inputString.replace(/'/g, '"');
  
    // Add double quotes to keys
    inputString = inputString.replace(/(\w+):/g, '"$1":');

    try {
      // Parse the string into a JSON object
      const jsonObject = JSON.parse(inputString);
    
      // return jsonObject;
      setConvertedOutput(JSON.stringify(jsonObject, null, 2));
    } catch (error) {
      console.error('Error converting Object to JSON:', error);
      showErrorMessage('Error converting Object to JSON')
    }
  }

  const handleBeautifulJson = () => {
    try {
      const json = JSON.parse(originalInput);
      setConvertedOutput(JSON.stringify(json, null, 2));
    } catch (error) {
      // showErrorMessage('Error JSON format')
    }
  }

  const handleMinifyJson = () => {
    try {
      const json = JSON.parse(originalInput);
      setConvertedOutput(JSON.stringify(json));
    } catch (error) {
      showErrorMessage('Error JSON format')
    }
  }

  const convertYamlToJson = () => {
    try {
      const jsonData = jsyaml.load(originalInput);
      setConvertedOutput(JSON.stringify(jsonData, null, 2));
    } catch (error) {
      console.error('Error converting YAML to JSON:', error);
      showErrorMessage('Error converting YAML to JSON')
    }
  };

  const convertJsonToYaml = () => {
    try {
      const jsonData = JSON.parse(originalInput);
      const yamlString = jsyaml.dump(jsonData);
      setConvertedOutput(yamlString);
      if (!yamlString) {
        showErrorMessage('Error converting JSON to YAML')
      }
    } catch (error) {
      console.error('Error converting JSON to YAML:', error);
      showErrorMessage('Error converting JSON to YAML')
    }
  }

  const convertCsvToJson = () => {
    try {
      csvToJson()
      .fromString(originalInput)
      .then((json) => {
        setConvertedOutput(JSON.stringify(json, null, 2));
      })
      .catch((error) => {
        console.error('Error converting CSV to JSON:', error);
        showErrorMessage('Error converting CSV to JSON')
      });
    } catch (error) {
      console.error('Error converting CSV to JSON', error);
      showErrorMessage('Error converting CSV to JSON')
    }
  }

  const convertJsonToCsv = () => {
    try {
      const csv = Papa.unparse(originalInput);
      if (!csv) {
        console.log('Error converting JSON to')
        showErrorMessage('Error converting JSON to CSV')
      }
      setConvertedOutput(csv);
    }  catch (error) {
      console.error('Error converting JSON to CSV', error);
      showErrorMessage('Error converting JSON to CSV')
    }
  }
  
  useEffect(() => {
    if (currentConvertName !== convertName) {
      setCurrentConvertName(convertName);
      setOriginalInput('');
      setConvertedOutput('');
    }
    if (originalInput) {
      handleConvertData(convertName)
    } else {
      setOriginalInput('');
      setConvertedOutput('');
    }
  }, [convertName, originalInput, convertedOutput, currentConvertName]);

    const copyToClipboard = () => {
      navigator.clipboard.writeText(convertedOutput);
    };

    const handleFileUpload = (event) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        setOriginalInput(content);
      };
      reader.readAsText(file);
    };

    const handleDownloadConvertedData = () => {
      let outputFiletype = null
      let outputFileExt = null
      switch(convertName) {
        case "YAML:JSON":
          outputFiletype = "application/json"
          outputFileExt = '.json'
          break;
        case "JSON:YAML":
          outputFiletype = "application/x-yaml"
          outputFileExt = '.yaml'
          break;
        default:
      }
      if (!outputFiletype) {
        return
      }
      const outputFileName = `Converted_${inputName}_To_${outputName}${outputFileExt}`
      const element = document.createElement("a");
      const file = new Blob([convertedOutput], { type: outputFiletype });
      element.href = URL.createObjectURL(file);
      element.download = outputFileName;
      document.body.appendChild(element);
      element.click()
    }

    return (
      <div className="grid grid-cols-1 my-4 xl:grid-cols-2 xl:gap-4">
        <div className="col-span-2 xl:col-auto">
          <div className="p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
            <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
              <h3 className='uppercase border-l-4 border-blue-600 p-2'>{inputName} Input</h3>
            </div>
            <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
              <Editor 
                height="50vh"
                value={originalInput}
                onChange={(value, event) => setOriginalInput(value)}
                language={convertDataTypeToEditorLanguage(inputName)}
                defaultValue={originalInput}
                theme={editorTheme}
                className="block p-2.5 w-full text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              />
            </div>
            <div className="px-4 py-2 text-gray-400rounded dark:border-gray-600">
              <input
                type="file"
                accept={convertDataTypeToFileExt(inputName)}
                onChange={handleFileUpload}
                className='hidden'
                name='file1'
              />
              { hasUploadFile && (
                <button
                  type="button"
                  onClick={() => document.querySelector('input[name="file1"]').click()}
                  className="text-white mr-2 bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
                >
                  <FontAwesomeIcon icon={faCloudArrowUp}/>
                  &nbsp; Browse your file
                </button>
              )}
              &nbsp;&nbsp;
              <button
                onClick={handleSwitch}
                type="button"
                className="text-white mt-4 bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
              >
                <FontAwesomeIcon icon={faExchangeAlt}/>
                &nbsp; Switch Mode
              </button>
              <button
                onClick={() => setOriginalInput('')}
                type="button"
                className="text-white bg-gray-400 hover:bg-gray-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
              >
                <FontAwesomeIcon icon={faEraser}/>
                &nbsp; Reset
              </button>
            </div>
          </div>
        </div>
        <div className="col-span-2 xl:col-auto">
          <div className="p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
            <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
              <h3 className='uppercase border-l-4 border-yellow-600 p-2'>{outputName} output</h3>
            </div>
            <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
              <Editor 
                height="50vh"
                value={convertedOutput}
                language={convertDataTypeToEditorLanguage(outputName)}
                defaultValue={convertedOutput}
                theme={editorTheme}
                className="block p-2.5 w-full text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
            </div>
            <div className="px-4 py-2 text-gray-400rounded dark:border-gray-600 text-right">
              <button
                onClick={copyToClipboard}
                type="button"
                className="text-white mt-4 bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
              >
                <FontAwesomeIcon icon={faCopy}/>
                &nbsp; Copy
              </button>
              <button
                onClick={handleDownloadConvertedData}
                type="button"
                className="text-white mt-4 bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
              >
                <FontAwesomeIcon icon={faCloudArrowDown}/>
                &nbsp; Download
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

export default ConvertDataType;
